import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateDataById } from '../../services/videos.service';
import QualityCheck from '../form-elements/quality-check.component';
import { CDN_BASE_URL } from '../../config/const.config';
import Upload from '../common/upload.component';

const VideoSystemAdmin = ({
  videoDetail,
  type,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      trailerUrl: videoDetail.trailerUrl || '',
      videoUrl: videoDetail.videoUrl || '',
      poster16x9Url: videoDetail.poster16x9Url || '',
      subtitleEnUrl: videoDetail.subtitleEnUrl || '',
      qualityVideo: videoDetail.qualityVideo || 'NC',
      qualityTrailer: videoDetail.qualityTrailer || 'NC',
      qualityPoster16x9: videoDetail.qualityPoster16x9 || 'NC',
      qualitySubtitle: videoDetail.qualitySubtitle || 'NC',
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [uploadedImg, setUploadedImg] = useState('');
  const [uploadedSrtUrl, setUploadedSrtUrl] = useState('');
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      ...data,
      type,
    };
    updateDataById(videoDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Assets data updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form id="system-admin-form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="videoUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Video URL"
                  type="text"
                  multiline
                  minRows={5}
                  maxRows={5}
                  error={!!errors.videoUrl}
                  helperText={errors?.videoUrl?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Upload
              videoId={videoDetail.id || 0}
              title="Upload Video"
              sourceType="video"
              s3Folder="videos/new"
              aspectRatio="16.9"
              allowedSize={[]}
              setSnackbarInfo={setSnackbarInfo}
              type="video"
            />
            <Typography variant="body2" color="secondary" mt={1}>
              ENC Status: {videoDetail.videoEncodeStatus?.toUpperCase() || ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Controller
              name="qualityVideo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <QualityCheck
                  id="quality_video"
                  name="quality_video"
                  label="Video URL Status"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.qualityVideo?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="trailerUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Trailer URL"
                  type="text"
                  multiline
                  minRows={5}
                  maxRows={5}
                  error={!!errors.trailerUrl}
                  helperText={errors?.trailerUrl?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Upload
              videoId={videoDetail.id || 0}
              title="Upload Trailer"
              sourceType="trailer"
              s3Folder="videos/new"
              aspectRatio="16.9"
              allowedSize={[]}
              setSnackbarInfo={setSnackbarInfo}
              type="video"
            />
            <Typography variant="body2" color="secondary" mt={1}>
              ENC Status:{' '}
              {videoDetail?.trailerEncodeStatus?.toUpperCase() || ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Controller
              name="qualityTrailer"
              control={control}
              render={({ field: { onChange, value } }) => (
                <QualityCheck
                  id="quality_trailer"
                  name="quality_trailer"
                  label="Trailer URL Status"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.qualityTrailer?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="poster16x9Url"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Poster 16x9 URL"
                  type="text"
                  multiline
                  minRows={5}
                  maxRows={5}
                  error={!!errors.poster16x9Url}
                  helperText={errors?.poster16x9Url?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
            <Typography variant="body2" color="secondary" mt={1}>
              {CDN_BASE_URL}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Upload
              title="Upload Poster 16x9"
              updateImage={(v) => {
                setValue('poster16x9Url', v);
                setUploadedImg(v);
              }}
              sourceType="poster16x9FileName"
              s3Folder="posters"
              aspectRatio="16.9"
              allowedSize={[[1920, 1080]]}
              setSnackbarInfo={setSnackbarInfo}
              type="poster"
              details={videoDetail}
            />
            {uploadedImg && (
              <img
                src={uploadedImg}
                style={{
                  maxHeight: '150px',
                  maxWidth: '150px',
                  objectFit: 'contain',
                }}
                alt="Uploaded"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Controller
              name="qualityPoster16x9"
              control={control}
              render={({ field: { onChange, value } }) => (
                <QualityCheck
                  id="quality_poster_16x9"
                  name="quality_poster_16x9"
                  label="Poster 16x9 URL Status"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.qualityPoster16x9?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="subtitleEnUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subtitle (EN) URL"
                  type="text"
                  multiline
                  minRows={5}
                  maxRows={5}
                  error={!!errors.subtitleEnUrl}
                  helperText={errors?.subtitleEnUrl?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
            <Typography variant="body2" color="secondary" mt={1}>
              {CDN_BASE_URL}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Upload
              title="Upload Subtitle (EN)"
              updateImage={(v) => {
                setValue('subtitleEnUrl', v);
                setUploadedSrtUrl(v);
              }}
              s3Folder="subtitles"
              sourceType="subtitleFileName"
              aspectRatio="16.9"
              allowedSize={[[1920, 1080]]}
              setSnackbarInfo={setSnackbarInfo}
              type="subtitle"
              details={videoDetail}
              isMultipleSubtitle
              subtitleLabel="English"
            />
            {uploadedSrtUrl && (
              <Box mt={5}>
                <Typography variant="body2" color="secondary" mt={1}>
                  {uploadedSrtUrl}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Controller
              name="qualitySubtitle"
              control={control}
              render={({ field: { onChange, value } }) => (
                <QualityCheck
                  id="quality_subtitle"
                  name="quality_subtitle"
                  label="Subtitle (EN) URL Status"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.qualitySubtitle?.message || ''}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="system-admin-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Assets
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

VideoSystemAdmin.propTypes = {
  videoDetail: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default VideoSystemAdmin;
